import * as React from "react";
import { useNavigate, useLocation, Outlet, useParams } from "react-router-dom";
import {
  AppBar,
  AppBarSection,
  AppBarSpacer,
  Avatar,
} from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { Drawer, DrawerContent } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import ErrorBoundary from "../components/ErrorBoundary";
import LoadingIndicator from "../components/LoadingIndicator";
import UserContext from "./UserContext";
import ClientHeader from "./ClientHeader";
import config from "../config";
import { PRIVILEGE_TYPE, FEATURE_KEYS } from "../constants/index";

import { useQueryGetUser } from "../data/services/authService";
import { useQueryGetActiveOEPeriod } from "../data/services/clientService";
import {
  hasStoredLocalAutoLoginToken,
  isLocalAutoLoginEnabled,
  isLocalDevelopmentHost,
  performLocalAutoLogin,
  shouldRefreshLocalAutoLogin,
} from "../utils/authRedirect";
import { useLogoutHandler } from "../utils/useLogoutHandler";
import useFeatureAccess from "../hooks/useFeatureAccess";
import {
  DEFAULT_BLOCKED_MESSAGE,
  useQueryGetBlockedMessage,
} from "../data/services/portalSettingService";

import OEContext from "./OEContext";

// Shown instead of the drawer/menu/Outlet when the client is on the
// FeatureAccessControl "blocked" list for this OE period. Server-enforced
// too - see BaseController.AuthorizeNonAdminClientAccess - this is the UI
// half so a blocked client never even sees the drawer.
//
// The text is admin-editable (Setup > Blocked Client Message). Fetched only
// when this renders, i.e. only for blocked clients, and falls back to the
// default text while loading or if the request fails.
const ClientBlockedMessage = () => {
  const { data } = useQueryGetBlockedMessage();
  const message = data?.data?.message || DEFAULT_BLOCKED_MESSAGE;

  return (
    <div
      data-testid="client-blocked-message"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "90vh",
        padding: "24px",
        textAlign: "center",
      }}
    >
      <p style={{ fontSize: "16px", color: "#192853", whiteSpace: "pre-line" }}>
        {message}
      </p>
    </div>
  );
};

// Sales Admins are read-only and get a deliberately narrow drawer: the
// Dashboard (view only), the three menu reports, and the User Guide. FSA
// Contribution is a submission screen, so it is excluded.
//
// This is an exclusion set rather than a second hand-built allow-list: a
// drawer item added later stays visible to Sales Admins unless someone
// deliberately excludes it here, rather than silently disappearing because it
// wasn't added to an allow-list.
const SALES_ADMIN_HIDDEN_ITEMS = ["FSA Contribution"];

// Removing hidden items can strand separators - two adjacent separators
// (a doubled divider), or a leading/trailing separator with nothing on the
// far side. Collapse runs of separators down to one, and drop a leading or
// trailing separator.
const removeHiddenDrawerItems = (drawerItems, hiddenTexts) => {
  const kept = drawerItems.filter(
    (item) => item.separator || !hiddenTexts.includes(item.text)
  );

  const normalized = [];
  kept.forEach((item) => {
    if (item.separator) {
      const previous = normalized[normalized.length - 1];
      if (!previous || previous.separator) {
        return;
      }
    }
    normalized.push(item);
  });

  if (normalized.length && normalized[normalized.length - 1].separator) {
    normalized.pop();
  }

  return normalized;
};

const ClientLayout = (props) => {
  const { clientId, oePeriodId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;
  const { loggedInUser, setLoggedInUser } = React.useContext(UserContext);

  const [drawerOpen, setDrawerOpen] = React.useState(true);
  const [isAutoSigningIn, setIsAutoSigningIn] = React.useState(false);
  const [hasAttemptedLocalAutoSignIn, setHasAttemptedLocalAutoSignIn] =
    React.useState(false);

  const avatarCompRef = React.useRef(null);
  const profilePopupCloseTimeoutRef = React.useRef(null);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = React.useState(false);
  const [user, setUser] = React.useState({});
  const [selectedOEPeriod, setSelectedOEPeriod] = React.useState(null);

  const [items, setItems] = React.useState([
    {
      text: "Dashboard",
      id: 1,
      selected: true,
    },
    {
      separator: true,
    },
    {
      text: "Full Rate Benefit Menus",
      id: 2,
    },
    /*
    {
      text: "Legacy Benefit Menus",
      id: 6,
    },
    */
    {
      text: "Rate Menu Comparisons",
      id: 5,
    },
    {
      separator: true,
    },
    {
      text: "Benefit Employee Menus",
      id: 3,
    },
    {
      separator: true,
    },
    {
      text: "FSA Contribution",
      id: 7,
    },
    {
      separator: true,
    },
    {
      text: "User Guide",
      id: 4,
    },
    {
      separator: true,
    },
  ]);

  const {
    data: userData,
    isLoading: userDataLoading,
    refetch: refetchUserData,
  } = useQueryGetUser();
  const { data: activeOEPeriodData, isLoading: activeOEPeriodDataLoading } =
    useQueryGetActiveOEPeriod(Boolean(userData?.data) && !isAutoSigningIn);
  const { logout } = useLogoutHandler();
  const isClientBlocked = useFeatureAccess(
    FEATURE_KEYS.Blocked,
    oePeriodId,
    clientId
  );

  const clearProfilePopupCloseTimeout = React.useCallback(() => {
    if (profilePopupCloseTimeoutRef.current) {
      window.clearTimeout(profilePopupCloseTimeoutRef.current);
      profilePopupCloseTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = () => {
    clearProfilePopupCloseTimeout();
    setIsProfilePopupOpen(true);
  };

  const handleMouseLeave = () => {
    clearProfilePopupCloseTimeout();
    profilePopupCloseTimeoutRef.current = window.setTimeout(() => {
      setIsProfilePopupOpen(false);
      profilePopupCloseTimeoutRef.current = null;
    }, 150);
  };

  React.useEffect(() => {
    let isMounted = true;

    const ensureUserSession = async () => {
      clearProfilePopupCloseTimeout();

      if (userDataLoading || isAutoSigningIn) {
        return;
      }

      if (
        isLocalDevelopmentHost() &&
        isLocalAutoLoginEnabled() &&
        !hasAttemptedLocalAutoSignIn &&
        (!userData?.data ||
          (hasStoredLocalAutoLoginToken() && shouldRefreshLocalAutoLogin()))
      ) {
        try {
          if (isMounted) {
            setIsAutoSigningIn(true);
            setHasAttemptedLocalAutoSignIn(true);
          }

          await performLocalAutoLogin();
          await refetchUserData();
        } catch (error) {
          navigate("/autherror");
        } finally {
          if (isMounted) {
            setIsAutoSigningIn(false);
          }
        }

        return;
      }

      if (isLocalDevelopmentHost() && !hasAttemptedLocalAutoSignIn) {
        setHasAttemptedLocalAutoSignIn(true);
      }

      if (!userData || !userData.data) {
        navigate("/login", { replace: true });
        return;
      }

      let userEntity = { ...userData.data };
      userEntity.hasDataSyncPriv = Boolean(userEntity.hasAdminAppPrivilege);
      if (userEntity.privileges && userEntity.privileges.length > 0) {
        userEntity.hasDataSyncPriv =
          userEntity.hasDataSyncPriv ||
          userEntity.privileges.some(
            (e) => e === PRIVILEGE_TYPE.HasDataSyncPrivilege
          );
        userEntity.hasUnlockPriv = userEntity.privileges.some(
          (e) => e === PRIVILEGE_TYPE.HasGridUnlockPrivilege
        );
        userEntity.submitContributionModalPrivilege =
          userEntity.privileges.some(
            (e) => e === PRIVILEGE_TYPE.SubmitContributionModalPrivilege
          );
        userEntity.brokerUserPrivilege = userEntity.privileges.some(
          (e) => e === PRIVILEGE_TYPE.BrokerUserPrivilege
        );
        userEntity.bdmUserPrivilege = userEntity.privileges.some(
          (e) => e === PRIVILEGE_TYPE.SalesUserPrivilege
        );
        userEntity.salesAdminPrivilege = userEntity.privileges.some(
          (e) => e === PRIVILEGE_TYPE.SalesAdminPrivilege
        );
        if (userEntity.bdmUserPrivilege) {
          userEntity.brokerUserPrivilege = false;
        }
      }

      setUser(userEntity);
      setLoggedInUser(userEntity);

      if (userEntity.hasAdminAppPrivilege) {
        navigate("/admin/dashboard");
      } else if (!userEntity.hasClientAppPrivilege) {
        navigate("/autherror");
      }
    };

    ensureUserSession();

    return () => {
      isMounted = false;
    };
  }, [
    clearProfilePopupCloseTimeout,
    hasAttemptedLocalAutoSignIn,
    isAutoSigningIn,
    navigate,
    refetchUserData,
    setLoggedInUser,
    userData,
    userDataLoading,
  ]);

  React.useEffect(() => {
    if (
      !activeOEPeriodDataLoading &&
      activeOEPeriodData &&
      activeOEPeriodData.data
    ) {
      setSelectedOEPeriod(activeOEPeriodData.data);
    }
  }, [activeOEPeriodData, activeOEPeriodDataLoading]);

  if (userDataLoading || activeOEPeriodDataLoading || isAutoSigningIn || !loggedInUser) {
    return <LoadingIndicator />;
  }

  const onSelect = (ev) => {
    const currentItem = ev.itemTarget.props;

    const newData = items.map((item) => {
      item.selected = item.id === currentItem.id;
      return item;
    });

    if (currentItem.route) {
      navigate(currentItem.route);
    }

    setItems(newData);
  };

  const onLogoutClick = async () => {
    await logout();
  };

  let selectedMenuItem = items.find(
    (e) => e.route === pathName || pathName.indexOf(e.route) > -1
  );
  let menuItems = [...items];
  if (selectedMenuItem) {
    menuItems = items.map((e) => {

      if (e.id === selectedMenuItem.id) {
        e.selected = true;
      } else {
        e.selected = false;
      }

      return e;
    });
  }

  if (selectedOEPeriod && clientId) {
    menuItems = menuItems.map((e) => {
      if (e.text === "Dashboard") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/summary`;
      } else if (e.text === "Full Rate Benefit Menus") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/planlibrary`;
      } else if (e.text === "Legacy Benefit Menus") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/legacy-planlibrary`;
      } else if (e.text === "Benefit Employee Menus") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/benefitempmenu`;
      } else if (e.text === "User Guide") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/userguide`;
      } else if (e.text === "Rate Menu Comparisons") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/menucomparisons`;
      } else if (e.text === "FSA Contribution") {
        e.route = `/clients/${clientId}/oe/${selectedOEPeriod.oePeriodId}/fsa`;
      }
      return e;
    });
  }

  if (user.salesAdminPrivilege) {
    menuItems = removeHiddenDrawerItems(menuItems, SALES_ADMIN_HIDDEN_ITEMS);
  }

  return (
    <React.Fragment>
      <AppBar
        themeColor={"light"}
        style={{ backgroundColor: "#192853", padding: 0 }}
      >
        <AppBarSection style={{ padding: "8px" }}>
          <img
            src={"/Logo_white.svg"}
            alt={"PrestigePEO logo"}
            style={{ height: 35, cursor: "pointer" }}
            onClick={() => navigate("/dashboard")}
          />
        </AppBarSection>

        <AppBarSpacer />

        <AppBarSection>
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => setIsProfilePopupOpen(!isProfilePopupOpen)}
            ref={avatarCompRef}
            className={
              isProfilePopupOpen ? "profile-avatar" : "profile-avatar-default"
            }
          >
            <span
              style={{
                paddingRight: "10px",
                paddingLeft: "8px",
                color: "#fff",
              }}
            >
              Welcome {user.userName}
            </span>
            <Avatar type="text" style={{ marginRight: "8px" }}>
              <span>{user.userInitial}</span>
            </Avatar>
          </div>
          <Popup
            anchor={avatarCompRef.current}
            show={isProfilePopupOpen}
            style={{ width: "180px" }}
          >
            <div
              style={{ padding: "10px" }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Button
                themeColor={"primary"}
                style={{ width: "100%" }}
                fillMode={"outline"}
                onClick={onLogoutClick}
              >
                {"Logout"}
              </Button>
            </div>
          </Popup>
        </AppBarSection>
      </AppBar>
      {clientId && isClientBlocked && (
        <OEContext.Provider value={selectedOEPeriod}>
          <ClientBlockedMessage />
        </OEContext.Provider>
      )}
      {clientId && !isClientBlocked && (
        <OEContext.Provider value={selectedOEPeriod}>
          <ClientHeader menuIconCLick={() => setDrawerOpen(!drawerOpen)} />
          <Drawer
            expanded={drawerOpen}
            mode="push"
            width={220}
            items={menuItems}
            onSelect={onSelect}
          >
            <DrawerContent
              style={{
                overflow: "auto",
                backgroundColor: "#EEF2F7",
                minHeight: "90vh",
              }}
            >
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
              <div style={{ height: "10px" }}></div>
            </DrawerContent>
          </Drawer>
        </OEContext.Provider>
      )}
      {!clientId && (
        <OEContext.Provider value={selectedOEPeriod}>
          <ErrorBoundary>
            <div style={{ margin: "15px" }}>
              <Outlet />
            </div>
          </ErrorBoundary>
          <div style={{ height: "10px" }}></div>
        </OEContext.Provider>
      )}
    </React.Fragment>
  );
};

export default ClientLayout;
